/**
 * ThemeManager utility functions
 */

// Check if we're inside an iframe (native detection)
export const getIsFrameMode = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.self !== window.top;
};

/**
 * Get the appropriate portal container for modals/panels.
 * When inside an iframe (frame mode), returns the parent window's document body
 * to allow panels to render outside the iframe's constraints (e.g., mobile preview frame).
 * Otherwise, returns the current document body.
 */
export const getPortalContainer = (): HTMLElement => {
  if (typeof window === "undefined") return document.body;

  // If inside iframe, portal to parent document to escape mobile frame constraints
  if (window.self !== window.top && window.parent?.document?.body) {
    return window.parent.document.body;
  }
  return document.body;
};

/**
 * Get the appropriate document for style injection.
 * When inside an iframe, returns the parent document so styles render correctly
 * for portaled elements.
 */
export const getPortalDocument = (): Document => {
  if (typeof window === "undefined") return document;

  // If inside iframe, use parent document for style injection
  if (window.self !== window.top && window.parent?.document) {
    return window.parent.document;
  }
  return document;
};

/**
 * Check if iframe can be used by inspecting CSP headers via HEAD request.
 * Returns false if Content-Security-Policy contains 'frame-ancestors none'
 * which blocks all iframe embedding.
 */
export const canUseIframe = async (): Promise<boolean> => {
  if (typeof window === "undefined") return true;

  try {
    const response = await fetch(window.location.href, { method: "HEAD" });
    const csp = response.headers.get("Content-Security-Policy");

    // Check for frame-ancestors 'none' which blocks all iframes
    if (csp?.includes("frame-ancestors 'none'")) {
      return false;
    }

    return true;
  } catch {
    // If check fails (e.g., CORS), try iframe anyway
    return true;
  }
};

// Get font format from file name
export const getFontFormat = (fileName: string): string => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "woff2":
      return "woff2";
    case "woff":
      return "woff";
    case "ttf":
      return "truetype";
    case "otf":
      return "opentype";
    default:
      return "woff2";
  }
};
